/**
 * API client for usage/time tracking endpoints
 */
import api from '../../axiosconfig';

/**
 * Record time spent on an interface
 * @param {Object} payload - Time tracking payload
 * @param {string} payload.interface - Interface name (e.g., 'chat')
 * @param {number} payload.seconds - Seconds to record
 * @param {string} [payload.startedAt] - Optional ISO timestamp
 * @param {string} [payload.endedAt] - Optional ISO timestamp
 * @returns {Promise<Object>} Response with ok status
 */
export const recordTimeSpent = async (payload) => {
  try {
    const response = await api.post('/api/usage/time-spent', payload);
    return response.data;
  } catch (error) {
    if (error?.response?.status === 404) {
      const fallbackResponse = await api.post('/v1/usage/time-spent', payload);
      return fallbackResponse.data;
    }
    throw error;
  }
};

/**
 * Get total time spent on an interface within a date range
 * @param {Object} params - Query parameters
 * @param {string} params.interface - Interface name
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @param {boolean} [params.includeBreakdown=false] - Include daily breakdown
 * @returns {Promise<Object>} Response with total time and optional breakdown
 */
export const getTotalTimeSpent = async (params) => {
  const { interface: interfaceName, startDate, endDate, includeBreakdown = false } = params;

  const queryParams = {
    interface: interfaceName,
    startDate,
    endDate,
    includeBreakdown: includeBreakdown ? 'true' : 'false'
  };

  try {
    const response = await api.get('/api/usage/time-spent/total', {
      params: queryParams
    });
    return response.data;
  } catch (error) {
    if (error?.response?.status === 404) {
      const fallbackResponse = await api.get('/v1/usage/time-spent/total', {
        params: queryParams
      });
      return fallbackResponse.data;
    }
    throw error;
  }
};

/**
 * Send time spent using beacon API (for page unload)
 * This is a fire-and-forget operation that works even when page is closing
 * @param {Object} payload - Time tracking payload
 * @param {string} payload.interface - Interface name
 * @param {number} payload.seconds - Seconds to record
 * @returns {boolean} Whether beacon was queued successfully
 */
export const sendTimeSpentBeacon = (payload) => {
  const API_URL = process.env.REACT_APP_API_URL;
  const url = `${API_URL}/api/usage/time-spent`;
  
  // Try to get token from Redux store (via window) or localStorage
  let token = null;
  
  try {
    // Try Redux store first (if window.__REDUX_STATE__ or similar is available)
    const reduxState = window.__store?.getState?.();
    token = reduxState?.auth?.accessToken;
  } catch (err) {
    // Ignore
  }
  
  // Fallback to localStorage/sessionStorage
  if (!token) {
    try {
      // Check if auth state is persisted in localStorage
      const persistedState = localStorage.getItem('persist:root');
      if (persistedState) {
        const parsed = JSON.parse(persistedState);
        const authData = JSON.parse(parsed.auth || '{}');
        token = authData.accessToken;
      }
    } catch (err) {
      // Ignore
    }
  }

  if (!token) {
    console.warn('No auth token available for beacon');
    return false;
  }

  // Use fetch with keepalive for reliable delivery on page unload
  try {
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload),
      keepalive: true // Ensures request continues even if page unloads
    }).catch(err => {
      // Errors during unload are expected, ignore
      console.warn('Failed to send time tracking on unload:', err);
    });
    return true;
  } catch (error) {
    console.warn('Error sending time tracking beacon:', error);
    return false;
  }
};

/**
 * Get company-wide interface usage (Admin only)
 * @param {Object} params - Query parameters
 * @param {string} params.interface - Interface name (chat, mindfulness, live_events, assessment)
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @param {boolean} [params.includeBreakdown=false] - Include per-user breakdown
 * @returns {Promise<Object>} Response with total usage and optional breakdown
 */
export const getCompanyInterfaceUsage = async (params) => {
  const { interface: interfaceName, startDate, endDate, includeBreakdown = false } = params;

  const queryParams = {
    interface: interfaceName,
    startDate,
    endDate
  };

  // Only include breakdown parameter if explicitly requested
  if (includeBreakdown) {
    queryParams.includeBreakdown = 'true';
  }

  const response = await api.get('/api/admin/usage', {
    params: queryParams
  });
  
  return response.data;
};

/**
 * Get company-wide total assessment questions answered (Admin/SuperUser only)
 * @param {Object} params - Query parameters
 * @param {string} params.fromDate - Start date/time (ISO or YYYY-MM-DD)
 * @param {string} params.toDate - End date/time (ISO or YYYY-MM-DD)
 * @returns {Promise<Object>} Response with questionsAnswered and related metadata
 */
export const getCompanyQuestionsAnswered = async (params) => {
  const { fromDate, toDate } = params;

  const response = await api.get('/api/admin/assessment-answers', {
    params: {
      fromDate,
      toDate,
    },
  });

  return response.data;
};

/**
 * Get company-level participation counts for assessment and live sessions (Admin/SuperUser only)
 * @param {Object} params - Query parameters
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @returns {Promise<Object>} Response with participant counts
 */
export const getCompanyAssessmentEngagement = async (params) => {
  const { startDate, endDate } = params;

  const response = await api.get('/api/admin/assessment-engagement', {
    params: {
      startDate,
      endDate,
    },
  });

  return response.data;
};

/**
 * Get company-level live session participation counts (Admin/SuperUser only)
 * @param {Object} params - Query parameters
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @returns {Promise<Object>} Response with participant count
 */
export const getCompanyLiveSessionEngagement = async (params) => {
  const { startDate, endDate } = params;

  const response = await api.get('/api/admin/live-session-engagement', {
    params: {
      startDate,
      endDate,
    },
  });

  return response.data;
};
